import axios from 'axios'

import { type GalleryItem as BackofficeGalleryItem } from '@/pages/gallery/gallery.type'

import {
  type PageChoice,
  type PageChoiceData,
  type PageContent,
  type PageContentLocation,
  parseServerPageContent,
  serializeServerPageContent,
  type ServerPageContent,
} from './pageContent.type'

// Round-trips go through parse/serializeServerPageContent — the axios calls
// are typed as `ServerPageContent` so the type system enforces it.
export const PageContentService = {
  async getAll(
    page: PageContentLocation,
    galleryRecord: Record<string, BackofficeGalleryItem>,
  ): Promise<PageContent[]> {
    const { data } = await axios.get<ServerPageContent[]>(
      `/api/pageContent/${page}/all`,
    )
    return data.map((c) => parseServerPageContent(c, galleryRecord))
  },
  // Returns void: no caller uses the response. Refetch via `getAll` if
  // needed (avoids threading galleryRecord through every save).
  async saveOne(homeContent: PageContent): Promise<void> {
    await axios.post<ServerPageContent>(
      '/api/pageContent',
      serializeServerPageContent(homeContent),
    )
  },
  async saveAll(
    pageContents: Omit<PageContent, 'page'>[],
    page: PageContentLocation,
    galleryRecord: Record<string, BackofficeGalleryItem>,
  ): Promise<PageContent[]> {
    const { data } = await axios.post<ServerPageContent[]>(
      `/api/pageContent/${page}/all`,
      // `page` is filled by the server from the URL; serialize ignores it.
      pageContents.map((pc) => serializeServerPageContent(pc as PageContent)),
    )
    return data.map((c) => parseServerPageContent(c, galleryRecord))
  },
  async deleteOne(id: string): Promise<void> {
    await axios.delete(`/api/pageContent/${id}`)
  },
  async getChoices(): Promise<PageChoice[]> {
    const { data } = await axios.get('/api/pageContent/choices')
    return data
  },
  async getAllEvents(): Promise<PageChoiceData[]> {
    const { data } = await axios.get('/api/pageContent/eventChoices')
    return data
  },
}
